/**
 * Phone country data for the COD order form — the ONE table the Form designer, its preview and the
 * storefront all read, so the three can never show the buyer a different country than the seller picked.
 *
 * Why it lives here and not in `ui-react`: the dial code used to be a private helper of the storefront
 * block, and the dashboard preview painted a HARDCODED Moroccan flag next to the phone field. A seller
 * selling in Romania saw 🇲🇦 in the designer, could not change it anywhere, and the storefront meanwhile
 * showed `+212` derived from the store country. Two sources, one of them a constant: the preview lied.
 *
 * `DIAL_CODES` is stored as one compact string (`"MA:212,FR:33,…"`) rather than an object literal so the
 * whole ISO-3166 list costs a few hundred bytes in every bundle that imports it.
 */
/**
 * ISO-3166 alpha-2 → E.164 country calling code, without the leading `+`.
 *
 * Hand-maintained (no dial-code dependency in the workspace). The markets we actually sell in — the
 * Maghreb, the Gulf, western Europe, north America — are the ones worth trusting without a second look;
 * a far-flung territory may carry a stale code, so treat a surprise there as a data fix, not a bug.
 */
const DIAL_TABLE = 
// Europe
'AD:376,AL:355,AT:43,AX:358,BA:387,BE:32,BG:359,BY:375,CH:41,CY:357,CZ:420,DE:49,DK:45,EE:372,' +
    'ES:34,FI:358,FO:298,FR:33,GB:44,GE:995,GG:44,GI:350,GR:30,HR:385,HU:36,IE:353,IM:44,IS:354,IT:39,' +
    'JE:44,LI:423,LT:370,LU:352,LV:371,MC:377,MD:373,ME:382,MK:389,MT:356,NL:31,NO:47,PL:48,PT:351,' +
    'RO:40,RS:381,RU:7,SE:46,SI:386,SJ:47,SK:421,SM:378,UA:380,VA:39,XK:383,' +
    // Africa
    'AO:244,BF:226,BI:257,BJ:229,BW:267,CD:243,CF:236,CG:242,CI:225,CM:237,CV:238,DJ:253,DZ:213,EG:20,' +
    'EH:212,ER:291,ET:251,GA:241,GH:233,GM:220,GN:224,GQ:240,GW:245,KE:254,KM:269,LR:231,LS:266,LY:218,' +
    'MA:212,MG:261,ML:223,MR:222,MU:230,MW:265,MZ:258,NA:264,NE:227,NG:234,RE:262,RW:250,SC:248,SD:249,' +
    'SH:290,SL:232,SN:221,SO:252,SS:211,ST:239,SZ:268,TD:235,TG:228,TN:216,TZ:255,UG:256,YT:262,ZA:27,' +
    'ZM:260,ZW:263,' +
    // Middle East & Asia
    'AE:971,AF:93,AM:374,AZ:994,BD:880,BH:973,BN:673,BT:975,CN:86,HK:852,ID:62,IL:972,IN:91,IQ:964,' +
    'IR:98,JO:962,JP:81,KG:996,KH:855,KP:850,KR:82,KW:965,KZ:7,LA:856,LB:961,LK:94,MM:95,MN:976,MO:853,' +
    'MV:960,MY:60,NP:977,OM:968,PH:63,PK:92,PS:970,QA:974,SA:966,SG:65,SY:963,TH:66,TJ:992,TL:670,' +
    'TM:993,TR:90,TW:886,UZ:998,VN:84,YE:967,' +
    // Americas
    'AG:1,AI:1,AR:54,AW:297,BB:1,BL:590,BM:1,BO:591,BQ:599,BR:55,BS:1,BZ:501,CA:1,CL:56,CO:57,CR:506,' +
    'CU:53,CW:599,DM:1,DO:1,EC:593,FK:500,GD:1,GF:594,GL:299,GP:590,GT:502,GY:592,HN:504,HT:509,JM:1,' +
    'KN:1,KY:1,LC:1,MF:590,MQ:596,MS:1,MX:52,NI:505,PA:507,PE:51,PM:508,PR:1,PY:595,SR:597,SV:503,' +
    'SX:1,TC:1,TT:1,US:1,UY:598,VC:1,VE:58,VG:1,VI:1,' +
    // Oceania
    'AS:1,AU:61,CC:61,CK:682,CX:61,FJ:679,FM:691,GU:1,KI:686,MH:692,MP:1,NC:687,NF:672,NR:674,NU:683,' +
    'NZ:64,PF:689,PG:675,PN:64,PW:680,SB:677,TK:690,TO:676,TV:688,VU:678,WF:681,WS:685';
const DIAL_CODES = Object.fromEntries(DIAL_TABLE.split(',').map((pair) => {
    const [code, dial] = pair.split(':');
    return [code, dial];
}));
/**
 * A number shape the buyer recognises, for the markets we sell in first. Unlisted countries fall back to
 * the dial code alone (see {@link phonePlaceholderFor}), which is still true and never misleading.
 */
const PHONE_EXAMPLES = {
    MA: '+212 6 00 00 00 00',
    DZ: '+213 6 00 00 00 00',
    TN: '+216 20 000 000',
    LY: '+218 91 000 0000',
    MR: '+222 22 00 00 00',
    EG: '+20 100 000 0000',
    SA: '+966 50 000 0000',
    AE: '+971 50 000 0000',
    QA: '+974 3000 0000',
    KW: '+965 500 00000',
    BH: '+973 3000 0000',
    OM: '+968 9200 0000',
    JO: '+962 7 9000 0000',
    LB: '+961 3 000 000',
    FR: '+33 6 12 34 56 78',
    ES: '+34 600 00 00 00',
    IT: '+39 320 000 0000',
    DE: '+49 151 00000000',
    BE: '+32 470 00 00 00',
    NL: '+31 6 12345678',
    GB: '+44 7400 000000',
    RO: '+40 721 000 000',
    PL: '+48 512 000 000',
    PT: '+351 912 000 000',
    US: '+1 (555) 000-0000',
    CA: '+1 (555) 000-0000',
    CI: '+225 01 00 00 00 00',
    SN: '+221 70 000 00 00',
};
/** Normalise whatever the config / the store handed us into an ISO-3166 alpha-2 key, or `''`. */
function normalise(code) {
    const c = (code ?? '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(c) ? c : '';
}
/** Every country we know a dial code for, sorted by code — the source of the designer's picker. */
export const PHONE_COUNTRY_CODES = Object.keys(DIAL_CODES).sort();
/**
 * The E.164 dial code for a country, e.g. `+212` / `+40`. Empty for an unknown country, in which case the
 * phone field shows no prefix box at all and the input runs full width — never a wrong `+212`.
 */
export function dialCodeFor(code) {
    const dial = DIAL_CODES[normalise(code)];
    return dial ? `+${dial}` : '';
}
/**
 * The country's flag as an emoji, derived from its two letters (regional indicator symbols) — no image,
 * no dependency. Empty for an unknown country, so the caller paints nothing rather than a placeholder box.
 */
export function flagEmojiFor(code) {
    const c = normalise(code);
    if (!c)
        return '';
    return String.fromCodePoint(...[...c].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
}
/** The full example number for a country (`+212 6 00 00 00 00`), or the dial code alone, or `''`. */
export function phonePlaceholderFor(code) {
    const c = normalise(code);
    return PHONE_EXAMPLES[c] ?? dialCodeFor(c);
}
/**
 * The example number WITHOUT its dial code (`6 00 00 00 00`) — what the input shows next to the prefix
 * box. Empty when we know no example, so the field falls back to its label rather than to a foreign shape.
 */
export function phoneNationalExampleFor(code) {
    const ex = PHONE_EXAMPLES[normalise(code)];
    return ex ? ex.replace(/^\+\d+\s*/, '') : '';
}
//# sourceMappingURL=phone-countries.js.map